import React, { useEffect, useRef, useState } from "react";

// Simple minimal canvas line chart (no external dependency)
const TimeseriesChart = ({
  labels,
  scans,
  redeems,
  pageViews = [],
  onSelect,
  showScans = true,
  showRedeems = true,
  showPageViews = false,
  metaSpend = [],
  metaReach = [],
  showMetaSpend = false,
  showMetaReach = false,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [hover, setHover] = useState(null); // {idx,x,y}
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const w = (canvas.width = canvas.clientWidth);
    const h = (canvas.height = canvas.clientHeight);
    ctx.clearRect(0, 0, w, h);
    const padLeft = 36; // reserve space for y-axis
    const padBottom = 22; // a bit more for x-labels
    const padTop = 4; // tighter to use more vertical space
    const padRight = 8;
    const innerW = w - padLeft - padRight;
    const innerH = h - padTop - padBottom;
    const seriesValues = [];
    if (showScans) seriesValues.push(...scans);
    if (showRedeems) seriesValues.push(...redeems);
    if (showPageViews && Array.isArray(pageViews)) seriesValues.push(...pageViews);
    if (showMetaSpend && Array.isArray(metaSpend)) seriesValues.push(...metaSpend);
    if (showMetaReach && Array.isArray(metaReach)) seriesValues.push(...metaReach);
    const maxVal = Math.max(1, ...(seriesValues.length ? seriesValues : [0]));
    const xs = labels.map(
      (_, i) => padLeft + innerW * (i / (labels.length - 1 || 1))
    );
    const scaleY = (v) => padTop + innerH - (v / maxVal) * innerH;
    // y-axis ticks
    const ticks = 4;
    const nice = (val) => val; // keep integer counts
    const isDark = document.documentElement.classList.contains('dark');
    const gridColor = isDark ? '#374151' : '#e5e7eb';
    const labelColor = isDark ? '#9ca3af' : '#6b7280';
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 1;
    for (let i = 0; i <= ticks; i++) {
      const v = (maxVal / ticks) * i;
      const y = scaleY(v);
      ctx.beginPath();
      ctx.moveTo(padLeft, y);
      ctx.lineTo(w - padRight, y);
      ctx.stroke();
      ctx.fillStyle = labelColor;
      ctx.font = "10px sans-serif";
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText(String(Math.round(nice(v))), padLeft - 4, y);
    }
    const drawLine = (data, color) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      data.forEach((v, i) => {
        const x = xs[i];
        const y = scaleY(v);
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      });
      ctx.stroke();
    };
    if (showScans) drawLine(scans, "#2563eb");
    if (showRedeems) drawLine(redeems, "#16a34a");
    if (showPageViews && Array.isArray(pageViews) && pageViews.length === labels.length) {
      drawLine(pageViews, "#6366f1");
    }
    if (showMetaSpend && Array.isArray(metaSpend) && metaSpend.length === labels.length) {
      drawLine(metaSpend, "#f97316");
    }
    if (showMetaReach && Array.isArray(metaReach) && metaReach.length === labels.length) {
      drawLine(metaReach, "#a855f7");
    }
    ctx.fillStyle = labelColor;
    ctx.font = "10px sans-serif";
    ctx.textAlign = "center";
    labels.forEach((lab, i) => {
      if (i % Math.ceil(labels.length / 6) === 0) {
        const txt = /\d{4}-\d{2}-\d{2}/.test(lab) ? lab.slice(5) : lab;
        ctx.fillText(txt, xs[i], h - 5);
      }
    });
    // hover crosshair
    if (hover && hover.idx != null) {
      const i = Math.max(0, Math.min(labels.length - 1, hover.idx));
      const x = xs[i];
      ctx.strokeStyle = isDark ? '#6b7280' : '#9ca3af';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, padTop);
      ctx.lineTo(x, h - padBottom);
      ctx.stroke();
      const y1 = scaleY(scans[i] || 0);
      const y2 = scaleY(redeems[i] || 0);
      if (showScans) {
        ctx.fillStyle = "#2563eb";
        ctx.beginPath(); ctx.arc(x, y1, 3, 0, Math.PI * 2); ctx.fill();
      }
      if (showRedeems) {
        ctx.fillStyle = "#16a34a";
        ctx.beginPath(); ctx.arc(x, y2, 3, 0, Math.PI * 2); ctx.fill();
      }
      if (showPageViews && Array.isArray(pageViews) && pageViews.length === labels.length) {
        const y3 = scaleY(pageViews[i] || 0);
        ctx.fillStyle = "#6366f1";
        ctx.beginPath(); ctx.arc(x, y3, 3, 0, Math.PI * 2); ctx.fill();
      }
      if (showMetaSpend && Array.isArray(metaSpend) && metaSpend.length === labels.length) {
        const y4 = scaleY(metaSpend[i] || 0);
        ctx.fillStyle = "#f97316";
        ctx.beginPath(); ctx.arc(x, y4, 3, 0, Math.PI * 2); ctx.fill();
      }
      if (showMetaReach && Array.isArray(metaReach) && metaReach.length === labels.length) {
        const y5 = scaleY(metaReach[i] || 0);
        ctx.fillStyle = "#a855f7";
        ctx.beginPath(); ctx.arc(x, y5, 3, 0, Math.PI * 2); ctx.fill();
      }
    }
  }, [labels, scans, redeems, pageViews, metaSpend, metaReach, showScans, showRedeems, showPageViews, showMetaSpend, showMetaReach, hover]);
  const handleClick = (e) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const w = canvasRef.current.clientWidth;
    const padLeft = 36; const padRight = 8;
    if (labels.length < 2) {
      if (onSelect) onSelect(labels[0]);
      return;
    }
    const segment = (w - padLeft - padRight) / (labels.length - 1);
    const idx = Math.round((x - padLeft) / segment);
    const label = labels[Math.min(labels.length - 1, Math.max(0, idx))];
    onSelect && onSelect(label);
  };
  const handleMove = (e) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const w = canvasRef.current.clientWidth;
    const padLeft = 36; const padRight = 8;
    if (labels.length < 2) { setHover(null); return; }
    const segment = (w - padLeft - padRight) / (labels.length - 1);
    const idx = Math.round((x - padLeft) / segment);
    setHover({ idx: Math.min(labels.length - 1, Math.max(0, idx)) });
  };
  const handleLeave = () => setHover(null);
  return (
    <div ref={containerRef} className="relative w-full h-52 md:h-64 lg:h-72">
      <canvas
        ref={canvasRef}
        onClick={handleClick}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        className="w-full h-full cursor-crosshair"
      />
      {hover && hover.idx != null && (
        <div className="absolute text-xs bg-card border rounded shadow px-2 py-1 pointer-events-none" style={{ left: 8, top: 8 }}>
          <div className="text-muted-foreground">{labels[hover.idx]}</div>
          {showScans && (
            <div><span className="inline-block w-2 h-2 bg-blue-600 rounded-full mr-1"/>Leads: {scans[hover.idx] ?? 0}</div>
          )}
          {showRedeems && (
            <div><span className="inline-block w-2 h-2 bg-green-600 rounded-full mr-1"/>Einlösungen: {redeems[hover.idx] ?? 0}</div>
          )}
          {showPageViews && Array.isArray(pageViews) && pageViews.length === labels.length && (
            <div><span className="inline-block w-2 h-2 rounded-full mr-1" style={{background:'#6366f1'}}/>Seitenaufrufe: {pageViews[hover.idx] ?? 0}</div>
          )}
          {showMetaSpend && Array.isArray(metaSpend) && metaSpend.length === labels.length && (
            <div><span className="inline-block w-2 h-2 bg-orange-500 rounded-full mr-1"/>Meta Budget: {metaSpend[hover.idx] ?? 0}</div>
          )}
          {showMetaReach && Array.isArray(metaReach) && metaReach.length === labels.length && (
            <div><span className="inline-block w-2 h-2 bg-purple-500 rounded-full mr-1"/>Meta Reichweite: {metaReach[hover.idx] ?? 0}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeseriesChart;
